<template>
  <div>
    <!-- 使用公共导航栏组件 -->
    <Nav />

    <!-- 主要内容区域 -->
    <main class="mt-20 min-h-screen">
      <!-- 页面标题区域 -->
      <section class="bg-white py-8 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-3xl font-bold text-gray-900 mb-2">租赁中心</h1>
              <p class="text-gray-600">专业会展设备租赁，满足您的各种需求</p>
            </div>
            <div class="flex items-center space-x-4">
              <div
                class="flex items-center space-x-2 px-1 py-1 bg-gray-100 rounded-full"
              >
                <!-- 网格视图按钮 加功能 -->
                <button
                  class="px-4 py-2 rounded-full text-gray-600 font-medium text-sm whitespace-nowrap view-toggle"
                  :class="{
                    'active bg-white text-primary': viewMode === 'grid',
                  }"
                  @click="switchToGridView"
                >
                  <i class="ri-grid-line mr-1"></i>网格视图
                </button>

                <button
                  class="px-4 py-2 rounded-full text-gray-600 font-medium text-sm whitespace-nowrap view-toggle"
                  :class="{
                    'active bg-white text-primary': viewMode === 'list',
                  }"
                  @click="switchToListView"
                >
                  <i class="ri-list-check mr-1"></i>列表视图
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div class="max-w-7xl mx-auto px-6 py-8">
        <div class="flex gap-8">
          <!-- 左侧筛选区域 -->
          <aside
            class="w-80 bg-white rounded-xl shadow-sm p-6 h-fit sticky top-28"
          >
            <!-- 搜索框 -->
            <div class="mb-6">
              <div class="relative">
                <input
                  type="text"
                  placeholder="搜索设备名称"
                  class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  v-model="searchKeyword"
                  @keyup.enter="handleSearch"
                />
                <div
                  class="absolute left-3 top-3 w-4 h-4 flex items-center justify-center"
                >
                  <i class="ri-search-line text-gray-400"></i>
                </div>
                <button
                  class="absolute right-3 top-2 bg-primary text-white px-3 py-1 rounded-md text-sm"
                  @click="handleSearch"
                >
                  搜索
                </button>
              </div>
            </div>
            <!-- 分类筛选 -->
            <div class="mb-6">
              <h3 class="font-semibold text-gray-900 mb-4">设备分类</h3>
              <div class="space-y-3" id="category-filter-container">
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="category-filter sr-only"
                    value="all"
                    :checked="selectedCategories.includes('all')"
                    @change="handleCategorySelect($event, 'all')"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                    :class="{
                      'bg-primary border-primary':
                        selectedCategories.includes('all'),
                      'border-gray-300': !selectedCategories.includes('all'),
                    }"
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: !selectedCategories.includes('all') }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700 category-text"
                    >全部分类</span
                  >
                  <span class="ml-auto text-xs text-gray-500"></span>
                </label>
                <template v-if="loading">
                  <!-- 加载状态 -->
                  <div class="flex items-center py-2">
                    <div
                      class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-3"
                    ></div>
                    <span class="text-sm text-gray-500">加载分类中...</span>
                  </div>
                </template>
                <template v-else>
                  <!-- 分类列表 -->
                  <label
                    v-for="category in categoryList"
                    :key="category.id"
                    class="flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      class="category-filter sr-only"
                      :value="category.id"
                      :checked="selectedCategories.includes(category.id)"
                      @change="handleCategorySelect($event, category.id)"
                    />
                    <div
                      class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                      :class="{
                        'bg-primary border-primary':
                          selectedCategories.includes(category.id),
                        'border-gray-300': !selectedCategories.includes(
                          category.id
                        ),
                      }"
                    >
                      <i
                        class="ri-check-line text-white text-xs"
                        :class="{
                          hidden: !selectedCategories.includes(category.id),
                        }"
                      ></i>
                    </div>
                    <span class="text-sm text-gray-700 category-text">{{
                      category.name
                    }}</span>
                    <span class="ml-auto text-xs text-gray-500"
                      >({{ category.goods_count || 0 }})</span
                    >
                  </label>
                </template>
              </div>
            </div>
            <!-- 价格筛选 -->
            <div class="mb-6">
              <h3 class="font-semibold text-gray-900 mb-4">价格范围</h3>
              <div class="space-y-3">
                <label class="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="price"
                    class="price-filter sr-only"
                    value="all"
                    :checked="priceRange === 'all'"
                    @change="handlePriceChange($event)"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded-full flex items-center justify-center mr-3 custom-radio"
                    :class="{
                      'bg-primary border-primary': priceRange === 'all',
                      'border-gray-300': priceRange !== 'all',
                    }"
                  >
                    <div
                      class="w-2 h-2 bg-primary rounded-full"
                      :class="{ hidden: priceRange !== 'all' }"
                    ></div>
                  </div>
                  <span class="text-sm text-gray-700">不限价格</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="price"
                    class="price-filter sr-only"
                    value="0-200"
                    :checked="priceRange === '0-200'"
                    @change="handlePriceChange($event)"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded-full flex items-center justify-center mr-3 custom-radio"
                    :class="{
                      'bg-primary border-primary': priceRange === '0-200',
                      'border-gray-300': priceRange !== '0-200',
                    }"
                  >
                    <div
                      class="w-2 h-2 bg-primary rounded-full"
                      :class="{ hidden: priceRange !== '0-200' }"
                    ></div>
                  </div>
                  <span class="text-sm text-gray-700">¥200 以下</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="price"
                    class="price-filter sr-only"
                    value="200-500"
                    :checked="priceRange === '200-500'"
                    @change="handlePriceChange($event)"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded-full flex items-center justify-center mr-3 custom-radio"
                    :class="{
                      'bg-primary border-primary': priceRange === '200-500',
                      'border-gray-300': priceRange !== '200-500',
                    }"
                  >
                    <div
                      class="w-2 h-2 bg-primary rounded-full"
                      :class="{ hidden: priceRange !== '200-500' }"
                    ></div>
                  </div>
                  <span class="text-sm text-gray-700">¥200 - ¥500</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="price"
                    class="price-filter sr-only"
                    value="500-1000"
                    :checked="priceRange === '500-1000'"
                    @change="handlePriceChange($event)"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded-full flex items-center justify-center mr-3 custom-radio"
                    :class="{
                      'bg-primary border-primary': priceRange === '500-1000',
                      'border-gray-300': priceRange !== '500-1000',
                    }"
                  >
                    <div
                      class="w-2 h-2 bg-primary rounded-full"
                      :class="{ hidden: priceRange !== '500-1000' }"
                    ></div>
                  </div>
                  <span class="text-sm text-gray-700">¥500 - ¥1000</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="price"
                    class="price-filter sr-only"
                    value="1000-9999999"
                    :checked="priceRange === '1000-9999999'"
                    @change="handlePriceChange($event)"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded-full flex items-center justify-center mr-3 custom-radio"
                    :class="{
                      'bg-primary border-primary':
                        priceRange === '1000-9999999',
                      'border-gray-300': priceRange !== '1000-9999999',
                    }"
                  >
                    <div
                      class="w-2 h-2 bg-primary rounded-full"
                      :class="{ hidden: priceRange !== '1000-9999999' }"
                    ></div>
                  </div>
                  <span class="text-sm text-gray-700">¥1000 以上</span>
                </label>
              </div>
            </div>
            <!-- 地区筛选 -->
            <!-- <div class="mb-6">
              <h3 class="font-semibold text-gray-900 mb-4">服务地区</h3>
              <div class="space-y-3">
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="region-filter sr-only"
                    value="all"
                    :checked="regions.length === 0"
                    @change="handleRegionChange($event, 'all')"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                    :class="{
                      'bg-primary border-primary': regions.length === 0,
                      'border-gray-300': regions.length > 0,
                    }"
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: regions.length > 0 }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700">全部地区</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="region-filter sr-only"
                    value="北京"
                    :checked="regions.includes('北京')"
                    @change="handleRegionChange($event, '北京')"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                    :class="{
                      'bg-primary border-primary': regions.includes('北京'),
                      'border-gray-300': !regions.includes('北京'),
                    }"
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: !regions.includes('北京') }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700">北京</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="region-filter sr-only"
                    value="上海"
                    :checked="regions.includes('上海')"
                    @change="handleRegionChange($event, '上海')"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                    :class="{
                      'bg-primary border-primary': regions.includes('上海'),
                      'border-gray-300': !regions.includes('上海'),
                    }"
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: !regions.includes('上海') }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700">上海</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="region-filter sr-only"
                    value="guangzhou"
                    :checked="regions.includes('guangzhou')"
                    @change="handleRegionChange($event, 'guangzhou')"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                    :class="{
                      'bg-primary border-primary':
                        regions.includes('guangzhou'),
                      'border-gray-300': !regions.includes('guangzhou'),
                    }"
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: !regions.includes('guangzhou') }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700">广州</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="region-filter sr-only"
                    value="shenzhen"
                    :checked="regions.includes('shenzhen')"
                    @change="handleRegionChange($event, 'shenzhen')"
                  />
                  <div
                    class="w-5 h-5 border-2 rounded flex items-center justify-center mr-3 custom-checkbox"
                    :class="{
                      'bg-primary border-primary': regions.includes('shenzhen'),
                      'border-gray-300': !regions.includes('shenzhen'),
                    }"
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: !regions.includes('shenzhen') }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700">深圳</span>
                </label>
              </div>
            </div> -->
            <!-- 重置筛选 -->
            <button
              class="w-full bg-gray-100 text-gray-700 py-3 !rounded-button hover:bg-gray-200 transition-colors whitespace-nowrap"
              @click="resetFilters"
            >
              重置筛选
            </button>
          </aside>
          <!-- 右侧产品列表区域 -->
          <div class="flex-1">
            <!-- 排序和结果统计 -->
            <div class="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div class="flex items-center justify-between">
                <div class="text-sm text-gray-600">
                  共找到
                  <span class="text-primary font-medium">{{ totalCount }}</span>
                  件设备
                </div>

                <div class="flex items-center space-x-4">
                  <div class="relative">
                    <button
                      id="sortBtn"
                      class="flex items-center space-x-2 bg-white border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
                      @click="toggleSortDropdown"
                    >
                      <span id="sortText">{{ sortText }}</span>
                      <div class="w-4 h-4 flex items-center justify-center">
                        <i class="ri-arrow-down-s-line text-gray-500"></i>
                      </div>
                    </button>
                    <div
                      id="sortDropdown"
                      class="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32"
                      :class="{ hidden: !showSortDropdown }"
                    >
                      <div class="py-1">
                        <button
                          v-for="option in sortOptions"
                          :key="option.key"
                          class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                          :class="{
                            'bg-blue-50 text-primary': sortBy === option.key,
                          }"
                          @click="handleSortChange(option.key)"
                        >
                          {{ option.label }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 产品网格/列表 -->
            <div
              id="product-grid"
              :class="{
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6':
                  viewMode === 'grid',
                'space-y-4': viewMode === 'list',
              }"
            >
              <!-- 产品卡片 -->

              <!-- 设备列表加载中 -->
              <div
                v-if="loading"
                class="col-span-full flex justify-center items-center h-64"
              >
                <div class="text-primary text-lg font-medium">加载中...</div>
              </div>

              <!-- 设备列表为空 -->
              <div
                v-else-if="goodsList.length === 0"
                class="col-span-full flex flex-col justify-center items-center h-64"
              >
                <i class="ri-information-line text-4xl text-gray-300 mb-4"></i>
                <div class="text-gray-500">暂无符合条件的设备</div>
              </div>

              <!-- 设备卡片列表 -->
              <div
                v-else
                class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow"
                :class="{
                  'product-card': viewMode === 'grid',
                  flex: viewMode === 'list',
                }"
                v-for="goods in goodsList"
                :key="goods.id"
                :data-category="goods.category_name"
                :data-price="goods.price"
                :data-region="goods.region"
              >
                <div class="relative group">
                  <img
                    :src="goods.thumbnail_url"
                    :alt="goods.name"
                    class="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <span
                    class="absolute top-3 left-3 bg-primary text-white px-2 py-1 text-xs rounded"
                    >{{ goods.category_name }}</span
                  >
                  <span
                    v-if="goods.is_recommend == 1"
                    class="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 text-xs rounded"
                    >热门</span
                  >

                  <div
                    class="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 quick-view-trigger cursor-pointer"
                    @click="handleQuickView(goods.id)"
                  >
                    <div class="bg-white rounded-full p-3">
                      <i class="ri-eye-line text-xl text-gray-700"></i>
                    </div>
                  </div>
                </div>
                <div class="p-5">
                  <h3
                    class="text-lg font-semibold text-gray-900 mb-2 flex items-center"
                  >
                    {{ goods.name }}
                    <span
                      v-if="goods.is_recommend == 1"
                      class="ml-2 bg-red-500 text-white px-2 py-1 text-xs rounded"
                      >热门</span
                    >
                  </h3>
                  <p class="text-sm text-gray-600 mb-3">
                    {{ goods.short_description || "暂无描述" }}
                  </p>
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center space-x-2">
                      <span class="text-2xl font-bold text-primary"
                        >¥{{ formatPrice(goods.price) }}</span
                      >
                      <span class="text-gray-500 text-sm">/天</span>
                    </div>
                    <!-- <div class="flex items-center text-sm text-gray-500">
                      <i class="ri-map-pin-line mr-1"></i>
                      <span>{{ goods.area || "全国" }}</span>
                    </div> -->
                  </div>
                  <div class="flex space-x-3">
                    <!-- 快速预览按钮 -->
                    <button
                      @click="handleQuickView(goods.id)"
                      class="flex-1 border border-gray-300 text-gray-700 py-2.5 px-4 !rounded-button hover:bg-gray-50 transition-colors text-sm whitespace-nowrap quick-view-btn"
                    >
                      快速预览
                    </button>
                    <button
                      @click="handleAddToCart(goods.id)"
                      class="flex-1 bg-primary text-white py-2.5 px-4 !rounded-button hover:bg-secondary transition-colors text-sm whitespace-nowrap add-to-cart-btn"
                    >
                      加入购物车
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- 使用分页组件 -->
            <Pagination
              :total-count="totalCount"
              :page_current="page_current"
              :page_size="page_size"
              :visible-range="2"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </main>

    <!-- 使用公共页脚组件 -->
    <Footer />
  </div>
</template>

<script>
import { Message } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import GoodsSelector from "@/components/GoodsSelector.vue";
import Pagination from "@/components/Pagination.vue";
import api from "@/utils/api.js";

export default {
  name: "Rent",
  components: {
    Nav,
    Footer,
    GoodsSelector,
    Pagination,
  },
  data() {
    return {
      categoryList: [],
      selectedCategories: ["all"],
      loading: false,
      goodsList: [], // 设备列表数据
      totalCount: 0, // 总设备数量
      page_current: 1, // 当前页码
      page_size: 12, // 每页显示数量
      // 排序相关
      sortBy: "default",
      showSortDropdown: false,
      // 排序选项配置
      sortOptions: [
        { key: "default", label: "默认排序" },
        { key: "price-low", label: "价格从低到高" },
        { key: "price-high", label: "价格从高到低" },
        { key: "popular", label: "热度排序" },
        { key: "newest", label: "最新上架" },
      ],
      priceRange: "all", // 价格范围
      regions: [], // 选中的地区
      viewMode: "grid", // 视图模式：grid 或 list
      searchKeyword: "", // 搜索关键词
    };
  },
  computed: {
    // 排序文本
    sortText() {
      switch (this.sortBy) {
        case "default":
          return "默认排序";
        case "price-low":
          return "价格从低到高";
        case "price-high":
          return "价格从高到低";
        case "popular":
          return "热度排序";
        case "newest":
          return "最新上架";
      }
    },
  },
  mounted() {
    // 从路由参数中获取 categoryIds
    const categoryIds = this.$route.params.categoryIds;
    if (categoryIds) {
      // 确保selectedCategories保持数组格式,还要选中对应分类
      this.selectedCategories = [categoryIds];
    }
    this.getCategoryList();
    this.getList(); // 初始加载设备列表
    // 添加点击外部关闭下拉菜单的事件监听
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    // 获取设备分类列表
    async getCategoryList() {
      try {
        this.loading = true;
        const response = await api.goods.getCategoryList();

        this.categoryList = response.data.categoryList || [];
      } catch (error) {
      } finally {
        this.loading = false;
      }
    },

    // 处理分类选择
    handleCategorySelect(e, category) {
      const isChecked = e.target.checked;

      // 全部分类的特殊处理
      if (category === "all") {
        if (isChecked) {
          // 选中全部分类，取消其他所有分类的选中状态
          this.selectedCategories = ["all"];
        } else {
          // 取消选中全部分类，保持其他分类的状态
          this.selectedCategories = this.selectedCategories.filter(
            (c) => c !== "all"
          );
        }
      } else {
        // 非全部分类的处理
        if (isChecked) {
          // 选中某个分类，取消全部分类的选中状态
          this.selectedCategories = this.selectedCategories.filter(
            (c) => c !== "all"
          );

          // 添加到已选中分类
          if (!this.selectedCategories.includes(category)) {
            this.selectedCategories.push(category);
          }
        } else {
          // 取消选中某个分类
          this.selectedCategories = this.selectedCategories.filter(
            (c) => c !== category
          );

          // 如果没有选中任何分类，自动选中全部分类
          if (this.selectedCategories.length === 0) {
            this.selectedCategories = ["all"];
          }
        }
      }

      // 筛选商品列表
      this.filterProducts();
    },

    // 处理价格范围选择
    handlePriceChange(e) {
      this.priceRange = e.target.value;
      this.filterProducts();
    },

    // 处理地区选择
    handleRegionChange(e, region) {
      const isChecked = e.target.checked;

      if (region === "all") {
        if (isChecked) {
          // 选中全部地区，清空选中的地区
          this.regions = [];
        }
      } else {
        if (isChecked) {
          // 添加到已选中地区
          if (!this.regions.includes(region)) {
            this.regions.push(region);
          }
        } else {
          // 取消选中某个地区
          this.regions = this.regions.filter((r) => r !== region);
        }
      }

      // 筛选商品列表
      this.filterProducts();
    },

    // 加载设备列表
    async getList() {
      try {
        this.loading = true;

        // 构建请求参数
        const params = {
          page_current: this.page_current,
          page_size: this.page_size,
          sortBy: this.sortBy,
        };

        // 添加搜索关键词
        if (this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim();
        }

        // 添加分类筛选条件（如果不是全部分类）
        if (
          this.selectedCategories.length > 0 &&
          this.selectedCategories[0] !== "all"
        ) {
          params.categoryIds = this.selectedCategories.join(",");
        }

        // 添加价格范围筛选
        if (this.priceRange !== "all") {
          params.priceRange = this.priceRange;
        }

        // 添加地区筛选
        if (this.regions.length > 0) {
          params.regions = this.regions.join(",");
        }

        // 调用API获取设备列表
        const response = await api.goods.getList(params);

        this.goodsList = response.data.resdata || [];
        this.totalCount = response.data.restotal || 0;
      } catch (error) {
        console.error("获取设备列表失败:", error);
        Message.error("获取设备列表失败，请稍后重试");
        this.goodsList = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    // 筛选商品列表
    filterProducts() {
      this.page_current = 1; // 重置到第一页
      this.getList(); // 重新加载商品列表
    },

    // 重置筛选条件
    resetFilters() {
      // 重置选中的分类
      this.selectedCategories = ["all"];

      // 重置其他筛选条件
      this.priceRange = "all";
      this.regions = [];
      this.searchKeyword = "";

      // 重新加载商品列表
      this.filterProducts();
    },

    // 处理搜索
    handleSearch() {
      this.page_current = 1;
      this.getList();
    },
    // 切换排序下拉菜单
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    // 处理排序变化
    handleSortChange(sortBy) {
      this.sortBy = sortBy;
      this.page_current = 1;
      this.getList();
      this.showSortDropdown = false;
    },
    // 处理文档点击，关闭下拉菜单
    handleDocumentClick(event) {
      const sortBtn = document.getElementById("sortBtn");
      const sortDropdown = document.getElementById("sortDropdown");

      // 如果点击的不是排序按钮或下拉菜单本身，则关闭下拉菜单
      if (
        sortBtn &&
        sortDropdown &&
        !sortBtn.contains(event.target) &&
        !sortDropdown.contains(event.target)
      ) {
        this.showSortDropdown = false;
      }
    },

    // 切换到网格视图
    switchToGridView() {
      this.viewMode = "grid";
    },

    // 切换到列表视图
    switchToListView() {
      this.viewMode = "list";
    },

    // 快速预览设备
    handleQuickView(id) {
      // 跳转到设备详情页
      this.$router.push({ name: "GoodsDetail", params: { id } });
    },

    // 处理分页变化
    handlePageChange(page) {
      this.page_current = page;
      this.getList();
    },

    // 添加到购物车
    async handleAddToCart(goodsId) {
      try {
        // 找到对应的商品信息
        const goods = this.goodsList.find((item) => item.id === goodsId);

        if (!goods) {
          Message.error("商品信息获取失败");
          return;
        }

        // 构建商品数据
        const cartItem = {
          sku_id: goods.sku_id || goods.id,
          name: goods.name,
          price: parseFloat(goods.price) || 0,
          quantity: 1,
          thumbnail_url: goods.thumbnail_url || "",
          supplier_name: goods.supplier_name || "未知供应商",
          supplier_id: goods.supplier_id || "",
          short_description: goods.short_description || "",
          goods_id: goods.id,
        };

        // 调用API添加商品到购物车
        const response = await api.goods.addToCart(cartItem);

        // 显示成功提示
        Message.success(`成功加入购物车`);
      } catch (error) {}
    },

    // 格式化价格
    formatPrice(price) {
      if (typeof price !== "number") {
        price = parseFloat(price) || 0;
      }
      return price.toLocaleString("zh-CN");
    },
  },
};
</script>

<style scoped>
/* 容器样式 */

/* 列表视图样式 */
.view-mode-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 列表视图中的产品卡片样式 */
.flex.product-card {
  display: flex !important;
  align-items: stretch;
  flex-direction: row;
}

.flex.product-card > .relative.group {
  width: 30%;
  max-width: 200px;
  flex-shrink: 0;
}

.flex.product-card > .p-5 {
  width: 70%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.flex.product-card .text-lg.font-semibold.text-gray-900 {
  font-size: 18px;
}

/* 列表视图中的热门标签移动到标题旁边 */
.flex.product-card .absolute.top-3.right-3.bg-red-500 {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/mainpage/Rent.vue